import { createSlice, PayloadAction, current } from "@reduxjs/toolkit";
import FlowDesignerState, { Clipboard, FlowNode, FlowEdge, PlacementAlgorithm } from "./flowdesignerslice.types";
import { applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange, Node } from "reactflow";
import { navigateToFlow } from "src/modules/flow-designer/react-flow/helpers/navigation.utils";
import { getClonedNode, removeSavedSelection } from "src/modules/flow-designer/react-flow/helpers/node.utils";

const initialState: FlowDesignerState = {
    nodes: [],
    edges: [],
    appFQN: null,
    namespace: null,
    currentFlowId: null,
    appStatus: null,
    appModel: null,
    group: null,
    selectedNode: null,
    clipboard: null,
    needsRefresh: false,
    isFetchingNodes: true,
    isEmpty: false,
    loadLocalErrors: false,
    exceptionsCount: 0,
    isAutomatedGroup: false,
    ui: {
        showMinimap: false,
        formType: "SIDEBAR",
        currentPlacementAlgorithm: "sugiyama",
        currentLayout: "TB",
        isRightSidebarOpen: false,
        isSidebarFullWidth: false,
        isExceptionsSidebarOpen: false,
        isTableProgressModalOpen: false,
        isMetadataBrowserOpen: false,
        showAppErrorsSidebar: false,
        showValidationErrorsSidebar: false,
        showProgressView: false,
        showAppSettingsSidebar: false,
        isDiscoverPIISidebarOpen: false,
        discoverPIIDefaultJob: null,
        isSentinelLiveMonitorSidebarOpen: false,
        sentinelLiveMonitorDefaultSentinel: null,
        appSettingsDeployment: false,
        isStreamPreviewVisible: false,
        isStreamGraphPreviewVisible: false,
        isStreamPreviewExpanded: false,
        isConversationalMode: false
    },
    streamPreviewData: []
};
const flowDesignerSlice = createSlice({
    name: "flowDesigner",
    initialState,
    reducers: {
        setNodesAction: (state, action: PayloadAction<FlowNode[] | null | undefined>) => {
            state.nodes = action.payload ?? [];
        },
        onNodesChangeAction: (state, action: PayloadAction<NodeChange[]>) => {
            state.nodes = applyNodeChanges(action.payload, state.nodes ?? []);
        },
        setEdgesAction: (state, action: PayloadAction<FlowEdge[] | null | undefined>) => {
            state.edges = action.payload ?? [];
        },
        onEdgesChangeAction: (state, action: PayloadAction<EdgeChange[]>) => {
            const currentEdges = state.edges ? current(state.edges) : [];
            state.edges = applyEdgeChanges(action.payload, currentEdges);
        },
        setAppFQNAction: (state, action: PayloadAction<FlowDesignerState["appFQN"]>) => {
            state.appFQN = action.payload;
        },
        setNamespaceAction: (state, action: PayloadAction<FlowDesignerState["namespace"]>) => {
            state.namespace = action.payload;
        },
        setCurrentFlowIdAction: (state, action: PayloadAction<FlowDesignerState["currentFlowId"]>) => {
            state.currentFlowId = action.payload;
        },
        setAppStatusAction: (state, action: PayloadAction<FlowDesignerState["appStatus"]>) => {
            state.appStatus = action.payload;
        },
        setAppModelAction: (state, action: PayloadAction<FlowDesignerState["appModel"]>) => {
            state.appModel = action.payload;
        },
        setGroupAction: (state, action: PayloadAction<FlowDesignerState["group"]>) => {
            state.group = action.payload;
        },
        setSelectedNodeAction: (state, action: PayloadAction<FlowDesignerState["selectedNode"]>) => {
            state.selectedNode = action.payload;
        },
        setIsRightSidebarOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isRightSidebarOpen = action.payload;
        },
        setIsSidebarFullWidthAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isSidebarFullWidth = action.payload;
        },
        toggleIsSidebarFullWidthAction: state => {
            state.ui.isSidebarFullWidth = !state.ui.isSidebarFullWidth;
        },
        closeRightSidebarAction: (
            state,
            action: PayloadAction<{ appName?: string; flowName?: string } | undefined>
        ) => {
            state.ui.isRightSidebarOpen = false;
            state.ui.isSidebarFullWidth = false;
            state.selectedNode = null;
            const { appName, flowName } = action.payload ?? {};
            removeSavedSelection(appName);
            navigateToFlow(appName, flowName);
        },
        setClipboardAction: (state, action: PayloadAction<Clipboard | null>) => {
            state.clipboard = action.payload;
        },
        deleteNodeAction: (state, action: PayloadAction<string>) => {
            state.nodes = (state.nodes ?? []).filter(node => node.id !== action.payload);
            state.edges = (state.edges ?? []).filter(
                edge => edge.source !== action.payload && edge.target !== action.payload
            );
        },
        cutNodeAction: (state, action: PayloadAction<Node[]>) => {
            const cutNodes = action.payload;
            const cutIds = new Set(cutNodes.map(n => n.id));
            state.clipboard = {
                nodes: cutNodes,
                isCut: true,
                sourceFlowId: state.currentFlowId,
                cutIds: Array.from(cutIds)
            };
            // Keep the nodes in place but dim them to 0.5 opacity. They are only
            // removed from the source flow when paste lands on a different flow
            // (handled by the backend metaObject flow update + recompile in handlePaste).
            state.nodes = (state.nodes ?? []).map(node =>
                cutIds.has(node.id) ? { ...node, selected: false, data: { ...node.data, isDimmed: true } } : node
            );
        },
        copyNodeAction: (state, action: PayloadAction<Node[]>) => {
            state.clipboard = { nodes: action.payload, isCut: false };
        },
        pasteClipboardNodeAction: (state, action: PayloadAction<string>) => {
            if (!state.clipboard) {
                return;
            }

            const { nodes: sourceNodes, isCut, sourceFlowId, cutIds } = state.clipboard;

            // Cut + paste on the same flow → undim the originals; don't duplicate them.
            if (isCut && sourceFlowId && sourceFlowId === state.currentFlowId && cutIds && cutIds.length > 0) {
                const cutIdSet = new Set(cutIds);
                state.nodes = (state.nodes ?? []).map(node =>
                    cutIdSet.has(node.id) ? { ...node, data: { ...node.data, isDimmed: false } } : node
                );
                state.clipboard = null;
                return;
            }

            const idSalt = action.payload;
            const newNodes: Node[] = sourceNodes.map((sourceNode, idx) => {
                const newId = `${sourceNode.id}-copy-${idSalt}-${idx}`;
                return isCut === true
                    ? {
                          ...sourceNode,
                          id: newId,
                          position: { x: sourceNode.position.x + 50, y: sourceNode.position.y + 50 },
                          data: { ...sourceNode.data, isDimmed: false }
                      }
                    : getClonedNode(sourceNode, newId, isCut);
            });

            newNodes.forEach(n => state.nodes.push(n as FlowNode));
            // Keep right-sidebar/selection logic single-node compatible: focus the last pasted node.
            state.selectedNode = newNodes[newNodes.length - 1] ?? null;
            state.ui.isRightSidebarOpen = newNodes.length === 1;
            state.clipboard = null;
        },
        setIsFetchingNodesAction: (state, action: PayloadAction<boolean>) => {
            state.isFetchingNodes = action.payload;
        },
        setIsEmptyAction: (state, action: PayloadAction<boolean>) => {
            state.isEmpty = action.payload;
        },
        setLoadLocalErrorsAction: (state, action: PayloadAction<boolean>) => {
            state.loadLocalErrors = action.payload;
        },
        setExceptionsCountAction: (state, action: PayloadAction<number>) => {
            state.exceptionsCount = action.payload;
        },
        setIsAutomatedGroupAction: (state, action: PayloadAction<boolean>) => {
            state.isAutomatedGroup = action.payload;
        },
        setShowMinimapAction: (state, action: PayloadAction<boolean>) => {
            state.ui.showMinimap = action.payload;
        },
        setFormTypeAction: (state, action: PayloadAction<FlowDesignerState["ui"]["formType"]>) => {
            state.ui.formType = action.payload;
        },
        setPlacementAlgorithmAction: (state, action: PayloadAction<PlacementAlgorithm>) => {
            state.ui.currentPlacementAlgorithm = action.payload;
        },
        setCurrentLayoutAction: (state, action: PayloadAction<FlowDesignerState["ui"]["currentLayout"]>) => {
            state.ui.currentLayout = action.payload;
        },
        setIsExceptionsSidebarOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isExceptionsSidebarOpen = action.payload;
        },
        setIsTableProgressModalOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isTableProgressModalOpen = action.payload;
        },
        setIsMetadataBrowserOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isMetadataBrowserOpen = action.payload;
        },
        setShowAppErrorsSidebarAction: (state, action: PayloadAction<boolean>) => {
            state.ui.showAppErrorsSidebar = action.payload;
        },
        setShowValidationErrorsSidebarAction: (state, action: PayloadAction<boolean>) => {
            state.ui.showValidationErrorsSidebar = action.payload;
        },
        setShowProgressViewAction: (state, action: PayloadAction<boolean>) => {
            state.ui.showProgressView = action.payload;
        },
        setShowAppSettingsSidebarAction: (state, action: PayloadAction<boolean>) => {
            state.ui.showAppSettingsSidebar = action.payload;
        },
        setAppSettingsDeploymentAction: (state, action: PayloadAction<boolean>) => {
            state.ui.appSettingsDeployment = action.payload;
        },
        setIsDiscoverPIISidebarOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isDiscoverPIISidebarOpen = action.payload;
        },
        setDiscoverPIIDefaultJobAction: (state, action: PayloadAction<string | null>) => {
            state.ui.discoverPIIDefaultJob = action.payload;
        },
        setIsSentinelLiveMonitorSidebarOpenAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isSentinelLiveMonitorSidebarOpen = action.payload;
        },
        setSentinelLiveMonitorDefaultSentinelAction: (state, action: PayloadAction<string | null>) => {
            state.ui.sentinelLiveMonitorDefaultSentinel = action.payload;
        },
        closeAllSidebarsAction: (state, action: PayloadAction<{ appName?: string; flowName?: string } | undefined>) => {
            state.ui.isRightSidebarOpen = false;
            state.ui.isSidebarFullWidth = false;
            state.ui.isExceptionsSidebarOpen = false;
            state.ui.isMetadataBrowserOpen = false;
            state.ui.showAppErrorsSidebar = false;
            state.ui.showValidationErrorsSidebar = false;
            state.ui.showAppSettingsSidebar = false;
            state.ui.isDiscoverPIISidebarOpen = false;
            state.ui.discoverPIIDefaultJob = null;
            state.ui.isSentinelLiveMonitorSidebarOpen = false;
            state.ui.sentinelLiveMonitorDefaultSentinel = null;
            state.ui.appSettingsDeployment = false;
            state.ui.isStreamPreviewVisible = false;
            state.ui.isStreamPreviewExpanded = false;
            state.selectedNode = null; // Clear selection when closing all sidebars
            const { appName, flowName } = action.payload ?? {};
            removeSavedSelection(appName);
            navigateToFlow(appName, flowName);
        },
        resetFlowDesignerStateAction: state => {
            return {
                ...initialState,
                // clipboard should persiste across flows
                clipboard: state.clipboard
            };
        },
        setStreamPreviewVisibleAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isStreamPreviewVisible = action.payload;
            if (!action.payload) {
                state.ui.isStreamPreviewExpanded = false;
            }
        },
        setStreamGraphPreviewVisibleAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isStreamGraphPreviewVisible = action.payload;
        },
        setStreamPreviewExpandedAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isStreamPreviewExpanded = action.payload;
        },
        setIsConversationalModeAction: (state, action: PayloadAction<boolean>) => {
            state.ui.isConversationalMode = action.payload;
        },
        clearStreamPreviewDataAction: state => {
            state.streamPreviewData = [];
        },
        triggerRefreshAction: state => {
            state.needsRefresh = true;
        },
        clearRefreshAction: state => {
            state.needsRefresh = false;
        }
    },
    extraReducers: builder => {
        builder.addCase("UPDATE_STREAM_PREVIEW_DATA", (state, action: PayloadAction<object>) => {
            const data = action.payload;
            if (state.streamPreviewData) {
                state.streamPreviewData.push(data);
            } else {
                state.streamPreviewData = [data];
            }
        });
        builder.addCase("CLEAR_STREAM_PREVIEW_DATA", state => {
            state.streamPreviewData = [];
        });
    }
});

export const {
    setNodesAction,
    onNodesChangeAction,
    setEdgesAction,
    onEdgesChangeAction,
    setAppFQNAction,
    setNamespaceAction,
    setCurrentFlowIdAction,
    setAppStatusAction,
    setAppModelAction,
    setGroupAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction,
    setIsSidebarFullWidthAction,
    toggleIsSidebarFullWidthAction,
    closeRightSidebarAction,
    setClipboardAction,
    deleteNodeAction,
    cutNodeAction,
    copyNodeAction,
    pasteClipboardNodeAction,
    setIsFetchingNodesAction,
    setIsEmptyAction,
    setLoadLocalErrorsAction,
    setExceptionsCountAction,
    setIsAutomatedGroupAction,
    setShowMinimapAction,
    setFormTypeAction,
    setPlacementAlgorithmAction,
    setCurrentLayoutAction,
    setIsExceptionsSidebarOpenAction,
    setIsTableProgressModalOpenAction,
    setIsMetadataBrowserOpenAction,
    setShowAppErrorsSidebarAction,
    setShowValidationErrorsSidebarAction,
    setShowProgressViewAction,
    setShowAppSettingsSidebarAction,
    setAppSettingsDeploymentAction,
    setIsDiscoverPIISidebarOpenAction,
    setDiscoverPIIDefaultJobAction,
    setIsSentinelLiveMonitorSidebarOpenAction,
    setSentinelLiveMonitorDefaultSentinelAction,
    closeAllSidebarsAction,
    resetFlowDesignerStateAction,
    setStreamPreviewVisibleAction,
    setStreamGraphPreviewVisibleAction,
    setStreamPreviewExpandedAction,
    clearStreamPreviewDataAction,
    triggerRefreshAction,
    clearRefreshAction,
    setIsConversationalModeAction
} = flowDesignerSlice.actions;

export default flowDesignerSlice.reducer;
